import { useEffect, useState } from 'react';
import { showToast } from '../../components/Toast';
import type { ContentDraft, DraftBlock } from '../../types';

type Props = {
  draft: ContentDraft;
  saving: boolean;
  onSave: (patch: { title: string; metaDescription: string; blocks: DraftBlock[] }) => Promise<void>;
  onRevert: () => Promise<void>;
  /** Approve for publishing. Does not publish: the publisher picks it up. */
  onApprove: () => Promise<void>;
  onClose: () => void;
};

/** Google truncates around here; past it the tail is written for nobody. */
const META_IDEAL = 155;
const TITLE_IDEAL = 60;

/**
 * Review and edit one drafted page.
 *
 * Edits the pipeline's BLOCK list rather than offering a rich-text box. That
 * looks like the more restrictive choice and is the opposite: `blocks` is
 * exactly what step4_publish.py converts into Gutenberg markup, so flattening
 * it to HTML here would break publishing, and parsing HTML back into blocks
 * would be a lossy round trip. A block's `type` stays fixed — it is what the
 * conversion switches on — so a heading cannot silently become a paragraph.
 */
export default function DraftEditorDrawer({
  draft, saving, onSave, onRevert, onApprove, onClose,
}: Props) {
  const [title, setTitle] = useState(draft.title ?? '');
  const [meta, setMeta] = useState(draft.metaDescription ?? '');
  const [blocks, setBlocks] = useState<DraftBlock[]>(draft.blocks);
  const [dirty, setDirty] = useState(false);

  // Re-seed when a different draft is opened in the same drawer.
  useEffect(() => {
    setTitle(draft.title ?? '');
    setMeta(draft.metaDescription ?? '');
    setBlocks(draft.blocks);
    setDirty(false);
  }, [draft.id, draft.title, draft.metaDescription, draft.blocks]);

  function editBlock(i: number, text: string) {
    setBlocks((prev) => prev.map((b, j) => (j === i ? { ...b, text } : b)));
    setDirty(true);
  }

  function editItem(bi: number, ii: number, text: string) {
    setBlocks((prev) =>
      prev.map((b, j) =>
        j === bi ? { ...b, items: (b.items ?? []).map((it: string, k: number) => (k === ii ? text : it)) } : b,
      ),
    );
    setDirty(true);
  }

  async function save() {
    try {
      await onSave({ title, metaDescription: meta, blocks });
      setDirty(false);
      showToast('Draft saved', { variant: 'success' });
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    }
  }

  async function revert() {
    if (!window.confirm('Discard all edits and restore what the pipeline wrote?')) return;
    try {
      await onRevert();
      showToast('Draft restored to the original', { variant: 'success' });
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    }
  }

  // Publishing replaces live public content on a page with no WordPress
  // revision history, so it asks first and names the page it will overwrite.
  // Unsaved edits are saved as part of the same action: approving reviewed copy
  // and then publishing different copy is the one thing this screen must not do.
  const canPublish = Boolean(draft.url) && !draft.publishedAt;
  const publishTitle = !draft.url
    ? 'This draft is not matched to a page, so there is nothing to replace'
    : draft.publishedAt
      ? 'Already published. Run a fresh refresh to replace this page again.'
      : `Replace ${new URL(draft.url).pathname} with this content`;

  async function publish() {
    if (!draft.url) return;
    const path = new URL(draft.url).pathname;
    const ok = window.confirm(
      `Replace the live content at ${path} with this draft?\n\n` +
        'The current page is backed up first, but these pages have no WordPress ' +
        'revision history, so that backup is the only way back.',
    );
    if (!ok) return;
    try {
      if (dirty) {
        await onSave({ title, metaDescription: meta, blocks });
        setDirty(false);
      }
      await onApprove();
      showToast('Approved — the publisher will replace the page shortly', { variant: 'success' });
      onClose();
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    }
  }

  const words = blocks.reduce(
    (n, b) => n + String(b.text ?? '').trim().split(/\s+/).filter(Boolean).length,
    0,
  );

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Draft editor">
      <div className="draft-drawer">
        <div className="draft-head">
          <div>
            <h3>{draft.city ? `${draft.city}${draft.state ? `, ${draft.state}` : ''}` : 'Draft'}</h3>
            <span className="field-help">
              {words.toLocaleString()} words · {blocks.length} blocks
              {draft.edited && ' · edited'}
              {draft.url ? ` · ${new URL(draft.url).pathname}` : ' · not matched to a page'}
            </span>
            {draft.publishedAt && (
              <span className="field-help draft-state is-published">
                Published {new Date(draft.publishedAt).toLocaleString()}
                {draft.publishedWpPostId ? ` · post ${draft.publishedWpPostId}` : ''}
              </span>
            )}
            {!draft.publishedAt && draft.approvedAt && (
              <span className="field-help draft-state is-approved">
                Approved {new Date(draft.approvedAt).toLocaleString()} — waiting for the publisher
              </span>
            )}
          </div>
          <button type="button" className="link-btn" onClick={onClose}>Close</button>
        </div>

        <div className="draft-body">
          <label>
            Page title
            <span className="field-help">
              {title.length} characters{title.length > TITLE_IDEAL && ' — Google will truncate this'}
            </span>
            <input value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} />
          </label>

          <label>
            Meta description
            <span className="field-help">
              {meta.length} characters{meta.length > META_IDEAL && ' — Google will truncate this'}
            </span>
            <textarea rows={3} value={meta}
                      onChange={(e) => { setMeta(e.target.value); setDirty(true); }} />
          </label>

          <div className="draft-blocks">
            {blocks.map((b, i) => (
              <div key={i} className={`draft-block is-${b.type}`}>
                <span className="draft-block-type">{b.type}</span>
                {b.type === 'list' && (b.items?.length ?? 0) > 0 ? (
                  <ul className="draft-list">
                    {(b.items ?? []).map((it: string, k: number) => (
                      <li key={k}>
                        <input value={it} onChange={(e) => editItem(i, k, e.target.value)} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <textarea
                    rows={b.type === 'heading' ? 1 : Math.max(2, Math.ceil((b.text?.length ?? 0) / 90))}
                    value={b.text ?? ''}
                    onChange={(e) => editBlock(i, e.target.value)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="draft-foot">
          {draft.edited && (
            <button type="button" className="link-btn" onClick={revert} disabled={saving}>
              Restore original
            </button>
          )}
          <span className="run-spacer" />
          <button type="button" className="secondary-btn" onClick={onClose} disabled={saving}>
            {dirty ? 'Discard' : 'Close'}
          </button>
          <button type="button" onClick={save} disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          {/* Publishing replaces a live page, so the button reads as the
              consequence rather than as "OK". Unsaved edits are saved first:
              approving stale copy and publishing something else is the one
              mistake this screen can make. */}
          <button
            type="button"
            className="publish-btn"
            onClick={publish}
            disabled={saving || !canPublish}
            title={publishTitle}
          >
            {saving ? 'Working…' : 'Publish to site'}
          </button>
        </div>
      </div>
    </div>
  );
}
